/* ═══════════════════════════════════════════════════════
   CrewCardSection — the Call Sheet, admin side (Docs & Files → Sheets)
   ═══════════════════════════════════════════════════════
   Job fields (blue tag) write through updateJobFields, the same path the Prep
   tab uses; card-only fields (purple tag) write to crewCards/{job}__{session}
   through saveCrewCard (field patches after the first save). Deploy is a
   server callable that reads both records back from Firestore, so what goes
   out is always what was saved.

   The form persists the subject it worked with on EVERY save (the client
   section's pattern): the server never detects a subject, it validates the
   stored value, so the two fingerprint the same sheet. `roster` goes into
   every snapshot here for the same reason: the server hashes the crew's
   phones, and a snapshot built without them would read "Changed since v1"
   forever.

   Layout: content on the left, a sticky status rail on the right (chip, gap
   meter, actions, recipients). Design: docs/crew-cards/DESIGN.md, sheets v2
   in docs/crew-cards/SHEETS-V2-SLICE-2-SPEC.md section 6.
*/
import React, { useEffect, useMemo, useState } from "react";
import { C, font, mono, inp, lbl, btnS, btnD } from "../../../../lib/styles.js";
import { fmtDate, FMT_DATE_LONG, sortSessions } from "../../../../lib/utils.js";
import { scopeJobToSession } from "../../../../lib/sessionScope.js";
import { useData } from "../../../../context/DataContext.jsx";
import { crewCardApi } from "../../../../lib/crewCardCallables.js";
import {
  CREW_ROLES_DEFAULT, crewCardId, cardSessionId, sessionForCard, blankCrewCard, computeGaps,
  seedChecklist, seedScope, buildSnapshot, fingerprintOf, otherSessionsOn, rosterEmailFor, itemKey, crewNamesOf, assignmentIndexOf,
  crewStatusRowFor, isSheetSessionEligible, presenceOf, normalizeGear,
  CAPTURE_SUBJECTS, CAPTURE_SUBJECT_LABELS, detectCaptureSubject, isChosenSubject,
} from "../../../../lib/crewCardRules.js";
import CrewCardView from "../../../../crewcard/CrewCardView.jsx";
import CardStatusRail from "./CardStatusRail.jsx";

const Tag = ({ kind, children }) => {
  const color = kind === "job" ? C.blue : kind === "card" ? C.purple : C.muted;
  return <span style={{ fontFamily: mono, fontSize: 9, letterSpacing: "0.08em", textTransform: "uppercase", padding: "1px 6px", borderRadius: 3, color, background: `${color}18` }}>{children}</span>;
};
const FL = ({ children, tag }) => <div style={{ ...lbl, marginBottom: 4, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", minWidth: 0 }}>{children}{tag && <Tag kind={tag}>{tag}</Tag>}</div>;
const small = { ...inp, fontSize: 12, padding: "7px 10px", boxSizing: "border-box", minWidth: 0 };
const ro = { ...small, background: C.surfaceAlt, color: C.muted };
const help = { fontSize: 11, color: C.muted, marginTop: 4, lineHeight: 1.4 };
const gapStyle = { borderColor: C.amber, background: `${C.amber}10` };
const grid4 = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px 14px" };
const grid3 = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "8px 14px" };
const grid2 = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px 14px" };
const td = { padding: "4px 6px 4px 0", verticalAlign: "top", borderBottom: `1px solid ${C.border}33` };
const th = { textAlign: "left", fontFamily: mono, fontSize: 9.5, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted, fontWeight: 500, padding: "2px 6px 6px 0", borderBottom: `1px solid ${C.border}` };
const tiny = { ...btnS, padding: "3px 8px", fontSize: 10 };
const chip = (on) => ({ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 7px 2px 5px", borderRadius: 999, border: `1px solid ${on ? C.text : C.border}`, background: on ? C.surfaceAlt : "transparent", color: on ? C.text : C.muted, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" });

function Group({ title, tag, children, last }) {
  return (
    <div style={{ padding: "10px 0", borderBottom: last ? "none" : `1px solid ${C.border}` }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
        <span style={{ ...lbl, margin: 0 }}>{title}</span>
        {tag && <Tag kind={tag}>{tag === "auto" ? "derived" : tag}</Tag>}
      </div>
      {children}
    </div>
  );
}

/* Style: the look of the sheet. Outside the fingerprint, so choosing one
   never flags "changed" and never resends. Same small buttons as the day picker. */
const STYLE_OPTIONS = [["normcore", "Normcore"], ["cool", "Cool"]];
function StyleControl({ value, onChange }) {
  const v = value === "cool" ? "cool" : "normcore";
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {STYLE_OPTIONS.map(([k, label]) => (
        <button key={k} type="button" aria-pressed={v === k} onClick={() => onChange(k)}
          style={{ ...tiny, background: v === k ? C.surfaceAlt : C.surface, color: v === k ? C.text : C.muted }}>{label}</button>
      ))}
    </span>
  );
}

/* A textarea over a string[] field, one line per entry. The text stays local
   while typing (a trailing newline would otherwise vanish on the next
   render); the card gets the trimmed non-empty lines. An outside change to
   the stored lines (another tab, a session switch) resets the text. */
const linesOf = (text) => String(text || "").split("\n").map(s => s.trim()).filter(Boolean);
function LinesArea({ lines, onChange, ...rest }) {
  const joined = (Array.isArray(lines) ? lines : []).map(s => String(s ?? "").trim()).filter(Boolean).join("\n");
  const [text, setText] = useState(joined);
  useEffect(() => { setText(t => (linesOf(t).join("\n") === joined ? t : joined)); }, [joined]);
  return <textarea {...rest} value={text} onChange={e => { setText(e.target.value); onChange(linesOf(e.target.value)); }} />;
}

export default function CrewCardSection({ job, updateJobFields, user }) {
  const { crewCards, saveCrewCard, flushJobSave, contacts, studioSettings, savedQuotes, jobs } = useData();
  const rosterStored = studioSettings?.crewRoster;
  const roster = useMemo(() => rosterStored || [], [rosterStored]);
  const roleOpts = studioSettings?.roleOpts?.length ? studioSettings.roleOpts : CREW_ROLES_DEFAULT;

  // Which session is this card for? Multi-day jobs get a day picker.
  const sessions = useMemo(() => sortSessions(job?.sessions || []), [job?.sessions]);
  const [sid, setSid] = useState(() => cardSessionId(job));
  const fallbackSid = cardSessionId(job);
  useEffect(() => { if (!sessions.some(s => s.id === sid) && sid !== "root") setSid(fallbackSid); }, [sessions, sid, fallbackSid]);

  const scope = job?.multiDay ? scopeJobToSession(job, sid) : null;
  const sj = scope?.scopedJob || job;
  const session = useMemo(() => sessionForCard(job, sid), [job, sid]);
  const patchJob = (p) => updateJobFields(scope ? scope.buildUpdate(p) : p);

  const quote = useMemo(() => (savedQuotes || []).find(q => q.id === job?.quoteId) || null, [savedQuotes, job?.quoteId]);
  const stored = useMemo(() => (crewCards || []).find(c => c.id === crewCardId(job.id, sid)) || null, [crewCards, job.id, sid]);
  const card = useMemo(() => {
    if (stored) return stored;
    const b = blankCrewCard(job.id, sid);
    return { ...b, checklist: seedChecklist({ job: sj, session, quote }), scope: seedScope({ job: sj, quote }), _seeded: true };
  }, [stored, job.id, sid, sj, session, quote]);
  /* The subject this form works with: the card's settled value, else the
     detection. Persisted on every save so the server fingerprints what the
     form showed (it never detects). */
  const subject = isChosenSubject(card.subject) ? card.subject : detectCaptureSubject({ job: sj, quote });
  const patchCard = (p) => {
    const { _seeded, ...rest } = card;
    void _seeded;
    return saveCrewCard({ ...rest, subject, ...p });
  };
  // The card as every save writes it, so gaps, snapshot and fingerprint see
  // the same sheet the server will.
  const cardAsSaved = useMemo(() => ({ ...card, subject }), [card, subject]);

  const { gaps, required, filled } = useMemo(() => computeGaps({ job: sj, session, card: cardAsSaved, roster }), [sj, session, cardAsSaved, roster]);
  const others = useMemo(() => otherSessionsOn(jobs, session?.date, job.id), [jobs, session?.date, job.id]);
  const snapshot = useMemo(() => buildSnapshot({ job: sj, session, card: cardAsSaved, contacts, roster, otherSessionsToday: others }), [sj, session, cardAsSaved, contacts, roster, others]);
  const confidential = snapshot.confidential;
  // The client block as the form shows it: the real values even when the
  // sheet withholds them (the snapshot blanks coordinator, day-of contact and
  // file recipients under confidential).
  const open = useMemo(() => (confidential ? buildSnapshot({ job: sj, session, card: { ...cardAsSaved, anonymizeClient: false }, contacts, roster }) : snapshot), [confidential, sj, session, cardAsSaved, contacts, roster, snapshot]);
  const fp = useMemo(() => fingerprintOf(snapshot), [snapshot]);
  const last = card.lastDeploy;
  const state = !last || last.revokedAt ? "none" : last.fingerprint === fp ? "sent" : "changed";
  const stateLabel = state === "none" ? (last?.revokedAt ? "Link revoked" : "Draft, not sent") : state === "sent" ? `Sent v${last.version}` : `Changed since v${last.version}`;
  const eligible = isSheetSessionEligible("crew", session);
  const startLabel = subject === "people" || !subject ? "Talent" : "Start";

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [preview, setPreview] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [attendance, setAttendance] = useState(null);

  const eventId = session?.gcalEventId || (sid === cardSessionId(job) ? job.gcalEventId : null);
  useEffect(() => {
    let cancelled = false;
    setAttendance(null);
    if (!eventId || !last) return;
    crewCardApi.attendance({ eventId }).then(r => { if (!cancelled) setAttendance(r?.attendees || []); }).catch(() => { if (!cancelled) setAttendance([]); });
    return () => { cancelled = true; };
  }, [eventId, last?.version]);

  const deploy = async () => {
    if (gaps.length || busy) return;
    setBusy(true); setMsg(null);
    try {
      // Flush pending card AND job writes before the server reads both back.
      const [saved, jobSaved] = await Promise.all([patchCard({}), flushJobSave ? flushJobSave(job.id) : Promise.resolve(true)]);
      if (!saved) throw new Error("the sheet did not save");
      if (!jobSaved) throw new Error("the job did not save");
      const r = await crewCardApi.deploy({ jobDocId: job.id, sessionId: sid, kind: "crew" });
      // warning: something the admin should know (several unrecorded earlier links, a new one sent).
      const warn = r.warning ? ` ${r.warning}` : "";
      if (r.unchanged) setMsg({ ok: true, warn: !!r.warning, text: `Already sent as v${r.version}; nothing has changed since.${warn}` });
      else setMsg({ ok: true, warn: !!r.warning, text: `Sent v${r.version} to ${r.sent} of ${r.sent + r.failed.length}${r.failed.length ? `. Failed: ${r.failed.join(", ")}` : "."}${r.calendarError ? " Calendar line not updated: " + r.calendarError : ""}${warn}` });
    } catch (e) {
      const g = e?.details?.gaps;
      setMsg({ ok: false, text: g?.length ? `Not sent. Missing: ${g.map(x => x.label).join("; ")}` : `Not sent: ${e.message}` });
    }
    setBusy(false);
  };
  const revoke = async () => {
    if (!last?.token || busy) return;
    setBusy(true); setMsg(null); setConfirmRevoke(false);
    try { await crewCardApi.revoke({ token: last.token }); setMsg({ ok: true, text: "Link revoked." }); }
    catch (e) { setMsg({ ok: false, text: `Could not revoke: ${e.message}` }); }
    setBusy(false);
  };
  const copyLink = async () => {
    if (!last?.url) return;
    if (!navigator?.clipboard?.writeText) { setMsg({ ok: false, text: `Copy failed. The link is ${last.url}` }); return; }
    try { await navigator.clipboard.writeText(last.url); setMsg({ ok: true, text: "Link copied." }); }
    catch { setMsg({ ok: false, text: `Copy failed. The link is ${last.url}` }); }
  };

  const crew = crewNamesOf(sj);
  const equipment = (sj.selectedEquipment?.length ? sj.selectedEquipment : sj.equipment) || [];
  const gapKeys = new Set(gaps.map(g => g.key));
  const rsvpFor = (name) => {
    if (!attendance) return "";
    const email = rosterEmailFor(roster, name);
    const a = attendance.find(x => (x.email || "").toLowerCase() === email);
    if (!a) return user?.email && email === user.email.toLowerCase() ? "organizer" : "";
    return { accepted: "Accepted", declined: "Declined", tentative: "Tentative", needsAction: "No response" }[a.responseStatus] || a.responseStatus;
  };
  // The row for the person's current roster address, else their newest row
  // (a corrected address adds a row; the old one stays as history).
  const statusFor = (name) => crewStatusRowFor(card.recipientStatus, roster, name);
  const fmtStamp = (iso) => iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  // The row is the one the twins read for the name (assignmentIndexOf: trimmed,
  // case-insensitive), updated in place with its stored crewName kept.
  const setAssignment = (name, patch) => {
    const list = [...(card.assignments || [])];
    const i = assignmentIndexOf(list, name);
    if (i >= 0) list[i] = { ...list[i], ...patch }; else list.push({ crewName: name, role: "", note: "", callTime: "", onSite: null, ...patch });
    patchCard({ assignments: list });
  };
  const setList = (key, i, patch) => patchCard({ [key]: (card[key] || []).map((x, j) => j === i ? { ...x, ...patch } : x) });
  const removeAt = (key, i) => patchCard({ [key]: (card[key] || []).filter((_, j) => j !== i) });
  const addTo = (key, item) => patchCard({ [key]: [...(card[key] || []), item] });
  const toggleGear = (i, row, name) => {
    const gear = normalizeGear(row.gear);
    setList("scope", i, { gear: gear.includes(name) ? gear.filter(g => g !== name) : [...gear, name] });
  };

  // Day-of contact: null = the coordinator (default), "" = none, else typed.
  // The input shows the effective value: the coordinator while default, else
  // the typed text as typed (not trimmed, or a space between words could not
  // be typed).
  const dayOfDefault = card.clientContact === null || card.clientContact === undefined;
  const dayOfNone = card.clientContact === "";
  const dayOfValue = dayOfDefault ? open.coordinator : String(card.clientContact);

  const rows = crew.map(name => {
    const st = statusFor(name);
    const rsvp = rsvpFor(name);
    const dl = st?.delivery;
    return [
      { text: name },
      { text: rsvp || (last ? (attendance === null ? "…" : "Not on the invite") : "—"), color: rsvp === "Accepted" ? C.green : rsvp === "Declined" ? C.danger : rsvp === "No response" ? C.amber : C.muted },
      { text: st ? `${dl || "sent"}${st.sentAt ? " " + fmtStamp(st.sentAt) : ""}${dl === "bounced" || dl === "failed" ? " · " + st.email : ""}${st.version !== last?.version ? ` (v${st.version})` : ""}` : "—", mono: true, color: dl === "bounced" || dl === "failed" ? C.danger : dl === "delivered" ? C.green : C.muted },
      { text: st ? (st.openedAt ? fmtStamp(st.openedAt) : "not yet") : "—", mono: true, italic: !st?.openedAt, color: st?.openedAt ? C.text : C.muted },
    ];
  });

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface, fontFamily: font }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>Call Sheet{session?.date ? ` · ${fmtDate(session.date, FMT_DATE_LONG)}` : ""}</span>
        <span style={{ fontSize: 11, color: C.muted }}>Blue fields are the job's own. Purple fields live on the sheet.</span>
        {job?.multiDay && sessions.length > 1 && (
          <span style={{ display: "flex", gap: 4, marginLeft: "auto" }}>
            {sessions.map(s => (
              <button key={s.id} type="button" onClick={() => setSid(s.id)} style={{ ...tiny, background: s.id === sid ? C.surfaceAlt : C.surface }}>{fmtDate(s.date) || "Undated"}</button>
            ))}
          </span>
        )}
        {eligible && (
          <div style={{ flexBasis: "100%", display: "flex", alignItems: "center", gap: "6px 18px", flexWrap: "wrap", fontSize: 11, color: C.muted }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ ...lbl, marginBottom: 0 }}>Style</span>
              <StyleControl value={card.style} onChange={style => patchCard({ style })} />
              <Tag kind="card">card</Tag>
              <span>Look of the sheet. Changing it never resends.</span>
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <span style={{ ...lbl, marginBottom: 0 }}>Subject</span>
              <select value={subject} onChange={e => patchCard({ subject: e.target.value })} style={{ ...small, width: "auto", minWidth: 110, padding: "3px 8px" }}>
                <option value="">Not sure</option>
                {CAPTURE_SUBJECTS.map(x => <option key={x} value={x}>{CAPTURE_SUBJECT_LABELS[x]}</option>)}
              </select>
              <Tag kind="card">card</Tag>
              <span>Talent or Start on the sheet, and the checklist wording on Reseed.</span>
            </span>
          </div>
        )}
      </div>

      {!eligible && (
        <div style={{ padding: "12px 14px", fontSize: 12, color: C.muted }}>No call sheet for a flight day.</div>
      )}

      {eligible && <div className="ccs-layout" style={{ padding: "0 12px 12px" }}>
        <div className="ccs-main" style={{ minWidth: 0 }}>
          <Group title="Session" tag="job">
            <div className="ccs-grid4" style={grid4}>
              <div><FL>Date</FL><div style={ro}>{session?.date ? fmtDate(session.date, FMT_DATE_LONG) : "No date. Set it on the Job Card."}</div></div>
              <div>
                <FL tag="card">Crew call</FL>
                <input type="time" value={card.crewCall || ""} onChange={e => patchCard({ crewCall: e.target.value })} style={{ ...small, ...(gapKeys.has("crewCall") ? gapStyle : {}) }} />
                <div style={help}>Optional. Crew must be here by. Moves the crew calendar event.</div>
              </div>
              <div><FL>{startLabel}</FL><input type="time" value={sj.callTime || ""} onChange={e => patchJob({ callTime: e.target.value })} style={{ ...small, ...(gapKeys.has("callTime") ? gapStyle : {}) }} /></div>
              <div><FL>Wrap</FL><input type="time" value={sj.wrapTime || ""} onChange={e => patchJob({ wrapTime: e.target.value })} style={small} /></div>
              <div style={{ gridColumn: "span 3" }}><FL>Location</FL><div style={{ ...ro, color: gapKeys.has("location") ? C.amber : C.muted }}>{snapshot.location.line || "No location. Set it on the Job Card."}</div></div>
              <div><FL>Transport</FL><div style={ro}>{sj.transport || "In Studio"}</div></div>
            </div>
          </Group>

          <Group title="Client and production" tag="job">
            <div className="ccs-grid3" style={grid3}>
              <div><FL>Client</FL><div style={ro}>{sj.clientName || "—"}</div></div>
              <div><FL>Production</FL><input value={sj.project || ""} onChange={e => patchJob({ project: e.target.value })} style={small} placeholder="Show or project name" /></div>
              <div><FL tag="auto">Coordinator</FL><div style={ro}>{open.coordinator || "Link a contact on the Job Card"}</div></div>
              <div>
                <FL tag="card">Day-of contact</FL>
                <input value={dayOfValue} onChange={e => patchCard({ clientContact: e.target.value })} style={small} placeholder={open.coordinator || "Nobody linked yet"} />
                <div style={{ ...help, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span>{dayOfDefault ? "The coordinator, by default." : dayOfNone ? "None on the sheet." : "Typed for this sheet."}</span>
                  {!dayOfDefault && <button type="button" style={tiny} onClick={() => patchCard({ clientContact: null })}>Use coordinator</button>}
                  {!dayOfNone && <button type="button" style={tiny} onClick={() => patchCard({ clientContact: "" })}>None</button>}
                </div>
              </div>
              <div><FL tag="auto">Receives files</FL><div style={ro}>{open.vendors || "Link a contact with role Vendor"}</div></div>
              <div><FL tag="card">Talent initials</FL><input value={card.talentInitials || ""} onChange={e => patchCard({ talentInitials: e.target.value })} style={small} placeholder="Optional, e.g. K.B." /></div>
              <div><FL tag="card">On site with talent</FL><input value={card.onSiteWithTalent || ""} onChange={e => patchCard({ onSiteWithTalent: e.target.value })} style={small} placeholder="e.g. production hair person" /></div>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, marginTop: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={confidential} onChange={e => patchCard({ anonymizeClient: e.target.checked })} style={{ accentColor: C.purple }} />
              <span><b>Anonymize client.</b> {confidential ? `Crew sees the job code only (${sj.jobId || "no code"}).` : "Off: crew sees client, production, coordinator, day-of contact, file recipients."}</span>
              <Tag kind="card">card</Tag>
            </label>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Replaces those on the sheet and in the email subject with the job code. A new sheet starts anonymized when the client is a person; ticking or unticking makes it this sheet's choice. Prep notes and open items are not scrubbed.</div>
          </Group>

          <Group title="Capture plan" tag="card">
            <div className="ccs-tablewrap"><table className="ccs-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr>{["", "What", "How", "Gear", ""].map((h, i) => <th key={i} style={th}>{h}</th>)}</tr></thead>
              <tbody>
                {(card.scope || []).map((s, i) => {
                  const gear = normalizeGear(s.gear);
                  // Gear stored on the row but no longer on the job stays tickable, so it can be removed.
                  const options = [...equipment, ...gear.filter(g => !equipment.includes(g))];
                  return (
                    <tr key={s.k || i}>
                      <td style={{ ...td, width: 28, fontFamily: mono, color: C.muted }}>{s.n}</td>
                      <td style={td}><input value={s.line || ""} onChange={e => setList("scope", i, { line: e.target.value })} style={small} placeholder="What is captured" /></td>
                      <td style={td}><input value={s.note || ""} onChange={e => setList("scope", i, { note: e.target.value })} style={small} placeholder="How, for the crew" /></td>
                      <td style={{ ...td, width: 220 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, paddingTop: 3 }}>
                          {options.length === 0 && <span style={{ fontSize: 11, color: C.amber }}>Pick gear on the Prep tab</span>}
                          {options.map(g => {
                            const on = gear.includes(g);
                            return (
                              <label key={g} style={chip(on)}>
                                <input type="checkbox" checked={on} onChange={() => toggleGear(i, s, g)} style={{ margin: 0, accentColor: C.text }} />
                                {g}
                              </label>
                            );
                          })}
                        </div>
                      </td>
                      <td style={{ ...td, width: 28 }}><button type="button" style={{ ...btnD, padding: "3px 6px", fontSize: 9 }} onClick={() => removeAt("scope", i)}>×</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button type="button" style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={() => addTo("scope", { k: itemKey(), n: String((card.scope || []).length + 1).padStart(2, "0"), line: "", note: "", gear: [] })}>+ Line</button>
              <button type="button" style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={() => patchCard({ scope: seedScope({ job: sj, quote }) })}>Reseed from quote</button>
            </div>
          </Group>

          <Group title="Notes" tag="job">
            <FL>Prep notes (the Prep tab's field, same text)</FL>
            <textarea value={sj.prepNotes || ""} onChange={e => patchJob({ prepNotes: e.target.value })} rows={6}
              style={{ ...small, resize: "vertical", lineHeight: 1.5, ...(gapKeys.has("prepNotes") ? gapStyle : {}) }} placeholder="Setup instructions, client requirements, what not to miss" />
            <div style={help}>Renders as a Notes list under the plan, one line per item.</div>
          </Group>

          <Group title="Assignments" tag="card">
            {crew.length === 0 && <div style={{ fontSize: 12, color: C.amber }}>No crew on this job yet. Add crew on the Prep tab.</div>}
            {crew.length > 0 && (
              <div className="ccs-tablewrap"><table className="ccs-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead><tr>{["Crew", "Role", "Note (the whole crew sees it)", "Call", "On site", "Email (roster)"].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {crew.map(name => {
                    const ai = assignmentIndexOf(card.assignments, name);
                    const a = ai >= 0 ? card.assignments[ai] : {};
                    const email = rosterEmailFor(roster, name);
                    const onSite = presenceOf(cardAsSaved, name);
                    return (
                      <tr key={name}>
                        <td style={{ ...td, whiteSpace: "nowrap" }}>{name}</td>
                        <td style={{ ...td, width: 170 }}>
                          <select value={a.role || ""} onChange={e => setAssignment(name, { role: e.target.value })} style={{ ...small, ...(gapKeys.has(`role:${name}`) ? gapStyle : {}) }}>
                            <option value="">Choose a role</option>{roleOpts.map(r => <option key={r} value={r}>{r}</option>)}
                          </select>
                        </td>
                        <td style={td}><input value={a.note || ""} onChange={e => setAssignment(name, { note: e.target.value })} style={small} placeholder="Optional" /></td>
                        <td style={{ ...td, width: 110 }}>
                          {onSite
                            ? <input type="time" value={a.callTime || ""} onChange={e => setAssignment(name, { callTime: e.target.value })} title="Optional. Their own call, else the crew call, else the session call." style={{ ...small, ...(gapKeys.has(`time:${name}`) ? gapStyle : {}) }} />
                            : <span style={{ fontSize: 11, color: C.muted }}>Off site</span>}
                        </td>
                        <td style={{ ...td, width: 60, textAlign: "center" }}>
                          <input type="checkbox" checked={onSite} onChange={e => setAssignment(name, { onSite: e.target.checked })} title="On site at the session. Data processing and Delivery roles are off site unless ticked." style={{ accentColor: C.purple, marginTop: 9 }} />
                        </td>
                        <td style={{ ...td, width: 180, fontFamily: mono, fontSize: 11, color: email ? C.muted : C.amber }}>{email || "No email on roster (Job Prep Settings)"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table></div>
            )}
            {crew.length > 0 && <div style={help}>Call is optional: their own time, else the crew call, else the {startLabel.toLowerCase()} call. On site shows the role's default until it is ticked or unticked; off-site people print in one line without a call.</div>}
          </Group>

          <Group title="Gear to prep" tag="job">
            <div className="ccs-grid2" style={grid2}>
              <div><FL>Equipment</FL><div style={{ ...ro, color: gapKeys.has("equipment") ? C.amber : C.muted }}>{equipment.length ? equipment.join(" · ") : "Pick gear on the Prep tab"}</div></div>
              <div><FL>Lenses · Additional items</FL><div style={ro}>{[...(sj.lenses || []), ...(sj.additionalItems || [])].join(", ") || "None (optional, Prep tab)"}</div></div>
            </div>
            <div style={{ marginTop: 8 }}>
              <FL tag="card">Extra gear</FL>
              <LinesArea key={card.id} lines={card.extraGear} onChange={extraGear => patchCard({ extraGear })} rows={3}
                style={{ ...small, resize: "vertical", lineHeight: 1.5 }} placeholder="One item per line, e.g. wig caps" />
              <div style={help}>One line per item. Prints with the equipment under Gear to prep.</div>
            </div>
          </Group>

          <Group title="Day-of checklist" tag="card">
            {(card.checklist || []).map((it, i) => (
              <div key={it.k || i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 0" }}>
                <input type="checkbox" checked={it.enabled !== false} onChange={e => setList("checklist", i, { enabled: e.target.checked })} title="Include on the sheet" />
                <input value={it.text || ""} onChange={e => setList("checklist", i, { text: e.target.value })} style={{ ...small, opacity: it.enabled === false ? 0.5 : 1 }} />
                <Tag kind="auto">{it.source === "auto" ? "seeded" : "manual"}</Tag>
                <button type="button" style={{ ...btnD, padding: "3px 6px", fontSize: 9 }} onClick={() => removeAt("checklist", i)}>×</button>
              </div>
            ))}
            <div style={{ display: "flex", gap: 6, marginTop: 6, alignItems: "center" }}>
              <button type="button" style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={() => addTo("checklist", { k: itemKey(), text: "", source: "manual", enabled: true })}>+ Item</button>
              <button type="button" style={{ ...btnS, padding: "4px 10px", fontSize: 10 }} onClick={() => patchCard({ checklist: [...seedChecklist({ job: sj, session, quote, subject }), ...(card.checklist || []).filter(x => x.source === "manual")] })}>Reseed</button>
              <span style={{ fontSize: 11, color: C.muted }}>One calibration line per rig on the job, then the capture log line. Manual items stay.</span>
            </div>
          </Group>

          <Group title="Deliverables" tag="job">
            <div className="ccs-grid3" style={grid3}>
              <div><FL>Format</FL><input value={sj.deliverableType || ""} onChange={e => patchJob({ deliverableType: e.target.value })} style={{ ...small, ...(gapKeys.has("deliverableType") ? gapStyle : {}) }} placeholder="e.g. Raw OBJ, no cleanup" /></div>
              <div><FL>Turnaround</FL><div style={ro}>{snapshot.deliverable.turnaround || "Set on the Delivery tab"}</div></div>
              <div><FL>Delivery</FL><div style={ro}>{snapshot.deliverable.delivery || "Set on the Delivery tab"}</div></div>
            </div>
          </Group>

          <Group title="Open items" tag="card" last>
            {(card.openItems || []).map((it, i) => (
              <div key={it.k || i} style={{ display: "flex", gap: 8, alignItems: "center", padding: "3px 0" }}>
                <input value={it.text || ""} onChange={e => setList("openItems", i, { text: e.target.value })} style={small} placeholder="Something to confirm on the day" />
                <button type="button" style={{ ...btnD, padding: "3px 6px", fontSize: 9 }} onClick={() => removeAt("openItems", i)}>×</button>
              </div>
            ))}
            <button type="button" style={{ ...btnS, padding: "4px 10px", fontSize: 10, marginTop: 6 }} onClick={() => addTo("openItems", { k: itemKey(), text: "" })}>+ Open item</button>
          </Group>
        </div>

        <CardStatusRail
          chip={{ state, label: stateLabel }} filled={filled} required={required} gaps={gaps}
          note={state === "none" && !gaps.length ? "Auto-deploys at 4:00 PM the day before." : (gaps.length ? "Complete the gaps to enable Deploy." : null)}
          msg={msg} busy={busy}
          onPreview={() => setPreview(true)} onCopy={copyLink} canCopy={!!last?.url && !last?.revokedAt}
          onRevoke={last?.token && !last?.revokedAt ? () => setConfirmRevoke(true) : null}
          onDeploy={deploy} deployLabel={state === "none" ? "Deploy" : "Redeploy"} deployDisabled={!!gaps.length}
          confirmRevoke={confirmRevoke} onConfirmRevoke={revoke} onCancelRevoke={() => setConfirmRevoke(false)}
          revokeText="Revoke the call sheet link? Everyone who has it loses access immediately."
          columns={["Crew", "Calendar", "Email", "Opened"]} rows={rows}
          footer={<>
            {last?.url && !last?.revokedAt ? <>Link: <span style={{ fontFamily: mono }}>{last.url}</span> · expires 3 days after the session. </> : null}
            {last?.calendarError ? <span style={{ color: C.amber }}>Calendar line not updated: {last.calendarError}. </span> : null}
            Calendar column reads the Google Calendar event. Email shows delivery as reported by Resend. Opened is reported by the sheet page once it renders. Questions arrive as replies to the sheet email.
          </>}
        />
      </div>}

      {preview && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(40,40,42,0.92)", display: "flex", flexDirection: "column" }} onClick={() => setPreview(false)}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.12)" }} onClick={e => e.stopPropagation()}>
            <span style={{ color: "#ddd", fontSize: 13 }}>Preview · what the crew will see{gaps.length ? ` · ${gaps.length} gap${gaps.length === 1 ? "" : "s"} still open` : ""}</span>
            <button type="button" style={btnS} onClick={() => setPreview(false)}>← Back</button>
          </div>
          <div style={{ flex: 1, overflow: "auto", padding: "24px 12px" }} onClick={e => e.stopPropagation()}>
            {/* 600px wide: over the sheet's 560px phone breakpoint (container width), so the page layout shows. */}
            <div style={{ maxWidth: 600, margin: "0 auto", borderRadius: 6, overflow: "hidden", boxShadow: "0 8px 60px rgba(0,0,0,0.5)" }}>
              <CrewCardView snap={snapshot} preview version={last?.version || 0} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
